import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import EdpiesLogo from '@/components/EdpiesLogo';
import {
  Shield,
  GraduationCap,
  Brain,
  Lock,
  Check,
  ArrowRight,
  ClipboardCheck,
  Target,
  BookOpen,
  TrendingUp,
  Trophy,
  Sparkles,
  Rocket,
  Leaf,
  Menu,
  X as XIcon,
  Star,
  Users,
  ChevronRight,
} from 'lucide-react';

// Hero photo (mother + son learning together with built-in progress overlays).
// Bundled in /public so it ships with the deploy and is served same-origin —
// avoids the customer-assets CDN being mis-cached or blocked by a CSP on
// production (edpies.com).
const HERO_IMG = '/edpies-hero.jpg';
const TUTOR_IMG = 'https://images.unsplash.com/photo-1610484826967-09c5720778c7?auto=format&fit=crop&w=1000&q=80';

const TIERS = [
  {
    id: 'foundation',
    name: 'Foundation',
    badge: 'GREAT FOR PRACTICE',
    headline: 'Foundation',
    pitch: 'Perfect for learners building strong foundations and preparing confidently for key academic milestones.',
    monthlyPrice: '5.99',
    annualPrice: '60',
    annualNote: 'Save over 15% annually',
    accent: 'from-sky-400 to-blue-500',
    btnClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    illo: Leaf,
    iconClass: 'text-sky-400',
    features: [
      'Access to curated learning resources based on your child\u2019s year group',
      'Practice papers across all core subjects',
      '4 mock papers per subject / year group',
      'Structured preparation for important academic milestones',
      'Pay-As-You-Go mocks — purchase additional mocks anytime',
    ],
    excludes: [
      'Growth Zone access',
      'AI-powered Challenger Series',
      'Advanced AI learning tools',
      'Unlimited mock papers',
    ],
  },
  {
    id: 'premium',
    name: 'Edpies Premium',
    badge: 'MOST POPULAR',
    headline: 'Personalised Success',
    pitch: 'Built for students who want personalised, adaptive learning with unlimited practice.',
    monthlyPrice: '12.99',
    annualPrice: '120',
    annualNote: 'Save over 20% annually',
    accent: 'from-purple-500 to-fuchsia-600',
    btnClass: 'bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white',
    illo: Rocket,
    iconClass: 'text-purple-400',
    popular: true,
    features: [
      'Everything in Foundation',
      'Unlimited mock papers',
      'Full access to AI-powered learning tools',
      'Growth Zone for personalised development & progress tracking',
      'AI Challenger Series for stretching high performers',
      'Smarter, adaptive learning that grows with the learner',
      'Enhanced insights into strengths and areas for improvement',
    ],
  },
];

const TRUST_ITEMS = [
  { icon: Shield, label: 'Trusted by Parents Across the UK', accent: 'text-emerald-600 bg-emerald-50' },
  { icon: GraduationCap, label: 'Designed for Academic Success', accent: 'text-purple-600 bg-purple-50' },
  { icon: Brain, label: 'AI-Powered Adaptive Learning', accent: 'text-amber-600 bg-amber-50' },
  { icon: Lock, label: 'Safe, Secure & Child Friendly', accent: 'text-sky-600 bg-sky-50' },
];

const HOW_IT_WORKS = [
  { n: 1, title: 'Take Adaptive Assessment', desc: 'We assess strengths and areas to improve.', icon: ClipboardCheck, color: 'from-emerald-400 to-emerald-500' },
  { n: 2, title: 'Get Your Smart Learning Path', desc: 'AI creates a personalised study plan.', icon: Target, color: 'from-purple-400 to-purple-500' },
  { n: 3, title: 'Practise with Papers & Tests', desc: 'Access resources, mock exams and quizzes.', icon: BookOpen, color: 'from-amber-400 to-amber-500' },
  { n: 4, title: 'Improve Scores Weekly', desc: 'Track progress and see real improvement.', icon: TrendingUp, color: 'from-sky-400 to-sky-500' },
  { n: 5, title: 'Real-Time Adaptive Learning', desc: "Through Ed's AI tutorial that grows with you.", icon: Brain, color: 'from-fuchsia-400 to-fuchsia-500' },
  { n: 6, title: 'Win Exam Confidence', desc: 'Build confidence and achieve your goals.', icon: Trophy, color: 'from-rose-400 to-rose-500' },
];

const TESTIMONIALS = [
  { name: 'Priya S.', loc: 'London, UK', quote: 'Edpies has completely transformed the way my daughter studies. The progress dashboard keeps me informed and she loves the practice tests!' },
  { name: 'James T.', loc: 'Surrey, UK', quote: "The adaptive learning is amazing! It focuses on my son's weak areas and his scores have improved so much in just a few weeks." },
  { name: 'Sarah M.', loc: 'Birmingham, UK', quote: 'Finally, a platform that is engaging for kids and gives parents real visibility. Highly recommended!' },
];

const HomePage = ({ isAuthenticated, progress }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const getDashboardPath = () => {
    if (!isAuthenticated || !progress?.user) return '/auth';
    if (progress.user.isAdmin) return '/admin/tutors';
    if (progress.user.userType === 'parent') return '/parent-dashboard';
    if (progress.user.userType === 'tutor') return '/tutor-dashboard';
    return '/welcome';
  };

  const handleStartTrial = () => {
    if (isAuthenticated) navigate(getDashboardPath());
    else navigate('/auth');
  };

  const handleChoosePlan = () => {
    if (isAuthenticated) navigate('/subscription');
    else navigate('/auth');
  };

  const scrollToPlans = () => {
    const el = document.getElementById('plans');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navItems = [
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Plans', href: '#plans' },
    { label: 'About Us', path: '/about' },
    { label: 'Resources', path: '/resources' },
    { label: 'Contact', path: '/contact' },
    { label: 'FAQ', path: '/faq' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-200" data-testid="home-page">
      {/* Navigation */}
      <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="nav-logo"
          >
            <EdpiesLogo size="sm" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
              Edpies
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  if (item.path) navigate(item.path);
                  else if (item.href === '#plans') scrollToPlans();
                  else if (item.href === '#how-it-works') document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors whitespace-nowrap"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g,'-')}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/auth')}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="nav-login-btn"
            >
              Log In
            </Button>
            <Button
              onClick={handleStartTrial}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
              data-testid="nav-start-trial-btn"
            >
              Start Free Trial
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          <button
            onClick={() => setMobileOpen(v => !v)}
            className="lg:hidden p-2 text-slate-300"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <XIcon className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-800 bg-slate-950 px-4 py-4 space-y-3" data-testid="nav-mobile-menu">
            {navItems.map(item => (
              <button
                key={item.label}
                onClick={() => {
                  setMobileOpen(false);
                  if (item.path) navigate(item.path);
                  else if (item.href === '#plans') scrollToPlans();
                  else if (item.href === '#how-it-works') document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="block w-full text-left text-sm font-medium text-slate-300 py-2"
              >
                {item.label}
              </button>
            ))}
            <div className="pt-2 space-y-2 border-t border-slate-800">
              <Button variant="outline" onClick={() => navigate('/auth')} className="w-full border-slate-700 text-slate-200 hover:bg-slate-800">Log In</Button>
              <Button onClick={handleStartTrial} className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white">Start Free Trial</Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-slate-900 to-indigo-900/20" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-20 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-5"
          >
            <motion.div
              className="inline-flex items-center gap-2 pl-3 pr-4 py-1.5 rounded-full bg-purple-950/70 border border-purple-500/40 shadow-lg shadow-purple-900/40 backdrop-blur-sm w-fit"
              data-testid="hero-kicker"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: [0, -6, 0] }}
              transition={{
                opacity: { duration: 0.4 },
                y: { duration: 3, repeat: Infinity, ease: 'easeInOut' }
              }}
            >
              <motion.span
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                className="inline-flex"
              >
                <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-300" strokeWidth={2.25} />
              </motion.span>
              <span className="text-[10px] sm:text-xs md:text-sm font-semibold tracking-wide text-white uppercase whitespace-nowrap" style={{ fontFamily: 'Inter, sans-serif' }}>
                UK's Growing AI-Powered Learning Platform
              </span>
            </motion.div>

            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-200" data-testid="hero-tagline" style={{ fontFamily: 'Space Grotesk' }}>
              Learning That Grows With You
            </h2>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Unlock Your Child's<br />
              <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">Full Potential</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-lg">
              AI-powered learning, mock exams, and expert resources that support your child across every stage.
            </p>

            {/* Key-stage coverage strip */}
            <div className="flex flex-wrap gap-2 pt-1" data-testid="key-stage-strip">
              {['7+', '11+', 'Year 9–10', 'GCSE', 'A-Levels'].map((stage) => (
                <span
                  key={stage}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-slate-800/60 border border-slate-700 text-slate-200 backdrop-blur"
                  data-testid={`stage-chip-${stage.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                >
                  <GraduationCap className="h-3 w-3 text-purple-400" />
                  {stage}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3 max-w-md pt-2">
              {[
                { icon: Brain, label: 'Personalised Learning' },
                { icon: ClipboardCheck, label: 'Real Exam Practice' },
                { icon: TrendingUp, label: 'Track Progress Effortlessly' },
              ].map((f, i) => (
                <div key={i} className="flex flex-col items-start gap-2">
                  <div className="p-2 rounded-lg bg-slate-800/70 border border-slate-700">
                    <f.icon className="h-4 w-4 text-purple-300" />
                  </div>
                  <p className="text-xs text-slate-400 font-medium leading-snug">{f.label}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={handleStartTrial}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white h-12 px-6 text-base font-semibold shadow-lg shadow-purple-600/30"
                data-testid="hero-start-trial-btn"
              >
                Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={scrollToPlans}
                className="h-12 px-6 text-base font-semibold border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"
                data-testid="hero-view-plans-btn"
              >
                View Plans
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-purple-900/40 aspect-[4/3] ring-1 ring-slate-700">
              <img src={HERO_IMG} alt="Mother and son learning together" className="w-full h-full object-cover" loading="eager" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-slate-900/60 border-y border-slate-800 py-8" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {TRUST_ITEMS.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-slate-800 border border-slate-700">
                  <Icon className="h-5 w-5 text-purple-400" />
                </div>
                <p className="text-sm font-medium text-slate-200 leading-tight">{t.label}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How Edpies Works */}
      <section id="how-it-works" className="py-20 px-4" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 space-y-2">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              How Edpies Works
            </h2>
            <p className="text-base md:text-lg text-slate-400">A simple path to success</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-4 relative">
            {/* Dashed connecting line (desktop only) */}
            <div className="hidden lg:block absolute top-12 left-[8%] right-[8%] h-px border-t-2 border-dashed border-slate-700" aria-hidden />
            {HOW_IT_WORKS.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.n} className="relative text-center" data-testid={`how-step-${step.n}`}>
                  <div className="relative inline-block">
                    <div className={`w-20 h-20 md:w-24 md:h-24 mx-auto rounded-2xl bg-gradient-to-br ${step.color} p-4 shadow-lg shadow-purple-900/40`}>
                      <Icon className="w-full h-full text-white" strokeWidth={1.5} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-800 border-2 border-slate-700 shadow flex items-center justify-center text-sm font-bold text-white">
                      {step.n}
                    </div>
                  </div>
                  <h3 className="font-bold text-white mt-4 text-sm md:text-base">{step.title}</h3>
                  <p className="text-xs md:text-sm text-slate-400 mt-1 max-w-[12rem] mx-auto">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="py-20 px-4 bg-slate-900/40 border-y border-slate-800" data-testid="plans-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 space-y-3">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Edpies Subscription Plans
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              Two simple tiers — one for confident practice, one for personalised, AI-powered learning. Cancel anytime.
            </p>
          </div>

          <div className="text-center mb-3">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-semibold uppercase tracking-wide">
              <GraduationCap className="h-3.5 w-3.5" />
              For 11+ Entry (Year 5–6)
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-testid="plans-11plus-grid">
            {TIERS.map((t) => {
              const Illo = t.illo;
              return (
                <div
                  key={t.id}
                  className={`relative bg-slate-800/50 backdrop-blur rounded-3xl border overflow-hidden transition-transform hover:-translate-y-1 ${t.popular ? 'border-purple-500/50 shadow-xl shadow-purple-900/40 ring-1 ring-purple-500/20' : 'border-slate-700/60 shadow-lg'}`}
                  data-testid={`plan-card-${t.id}`}
                >
                  {t.badge && (
                    <div className={`absolute top-0 left-1/2 -translate-x-1/2 ${t.popular ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500' : 'bg-slate-700'} text-white text-xs font-bold px-5 py-1.5 rounded-b-xl flex items-center gap-1.5 shadow-lg whitespace-nowrap`}>
                      {t.popular ? <Star className="h-3 w-3 fill-current" /> : <Sparkles className="h-3 w-3" />}
                      {t.badge}
                    </div>
                  )}

                  <div className="p-6 md:p-8 pt-12">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{t.name}</p>
                        <h3 className={`text-2xl md:text-3xl font-bold mt-1 ${t.popular ? 'text-purple-300' : 'text-cyan-300'}`} style={{ fontFamily: 'Space Grotesk' }}>
                          {t.headline}
                        </h3>
                        <p className="text-sm text-slate-300 mt-1.5">{t.pitch}</p>
                      </div>
                      <div className="p-3 rounded-2xl bg-slate-900/60 border border-slate-700">
                        <Illo className={`h-8 w-8 ${t.iconClass}`} />
                      </div>
                    </div>

                    <div className="my-6 pb-6 border-b border-slate-700">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="text-4xl font-bold text-white">£{t.monthlyPrice}</span>
                        <span className="text-slate-400">/month</span>
                        <span className="text-slate-500 mx-1">·</span>
                        <span className="text-2xl font-bold text-emerald-300">£{t.annualPrice}</span>
                        <span className="text-slate-400">/year</span>
                        <p className="basis-full text-xs text-emerald-400 mt-1">{t.annualNote}</p>
                      </div>
                    </div>

                    <ul className="space-y-2.5 mb-4">
                      {t.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm">
                          <Check className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-300">{f}</span>
                        </li>
                      ))}
                    </ul>

                    {t.excludes && (
                      <div className="mb-6 pt-4 border-t border-slate-700/60">
                        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Not included</p>
                        <ul className="space-y-2">
                          {t.excludes.map((f, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-sm">
                              <XIcon className="h-4 w-4 text-rose-400/80 flex-shrink-0 mt-0.5" />
                              <span className="text-slate-400">{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Button
                      onClick={handleChoosePlan}
                      className={`w-full h-11 font-semibold ${t.btnClass}`}
                      data-testid={`plan-cta-${t.id}`}
                    >
                      Choose {t.name}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* All Other Year Groups — single all-access plan */}
          <div className="mt-12 max-w-4xl mx-auto" data-testid="plan-other-year-groups">
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-wide">
                <GraduationCap className="h-3.5 w-3.5" />
                For 7+, KS3 &amp; GCSE
              </span>
            </div>
            <div className="relative bg-gradient-to-br from-emerald-500/10 to-teal-500/10 backdrop-blur rounded-3xl border border-emerald-500/30 shadow-lg p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-shrink-0">
                <div className="p-4 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 inline-block">
                  <Trophy className="h-10 w-10 text-emerald-300" />
                </div>
              </div>
              <div className="flex-1 space-y-2">
                <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold">All-Access Plan</p>
                <h3 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                  One simple plan — everything included
                </h3>
                <p className="text-sm text-slate-300">
                  Unlimited practice, mock papers, AI-powered learning, Growth Zone, Adaptive Learning, AI Challenger Series and more — all in one plan for 7+ Entry, KS3 and GCSE learners.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-2">
                  <span className="text-4xl font-bold text-white">£5.99</span>
                  <span className="text-slate-400">/month</span>
                  <span className="text-slate-500 mx-1">·</span>
                  <span className="text-2xl font-bold text-emerald-300">£60</span>
                  <span className="text-slate-400">/year</span>
                  <span className="text-xs text-emerald-400 font-medium ml-1">Save over 15% annually</span>
                </div>
              </div>
              <div className="flex-shrink-0 w-full md:w-auto">
                <Button
                  onClick={handleChoosePlan}
                  className="w-full md:w-auto h-12 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-semibold"
                  data-testid="plan-cta-all-access"
                >
                  Get All-Access
                </Button>
              </div>
            </div>
            <p className="text-center text-xs text-slate-400 mt-3" data-testid="a-levels-note">
              A-Levels subscriptions launching soon — explore A-Level content free in the meantime.
            </p>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/subscription')}
              className="text-sm font-semibold text-purple-300 hover:text-purple-200 inline-flex items-center gap-1"
              data-testid="compare-features-link"
            >
              See full pricing details <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Parents Stay Informed + Need Extra Support */}
      <section className="py-20 px-4 space-y-8" data-testid="parents-support-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
          {/* Parents Stay Informed */}
          <div className="bg-slate-800/40 backdrop-blur rounded-3xl border border-slate-700/60 p-8 flex flex-col md:flex-row gap-6 items-center">
            <div className="flex-1 space-y-4">
              <h3 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Parents Stay Informed, Always</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                {['Real-time performance updates', 'Strength & weakness insights', 'Study recommendations', 'Track progress anytime, anywhere'].map((it) => (
                  <li key={it} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-cyan-400 mt-0.5" /> {it}
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                onClick={() => navigate('/auth')}
                className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent"
                data-testid="parent-dashboard-cta"
              >
                See Parent Dashboard
              </Button>
            </div>
            {/* Faux dashboard preview */}
            <div className="w-full md:w-56 flex-shrink-0">
              <div className="bg-slate-900/80 border border-slate-700 rounded-2xl shadow-lg p-4 space-y-3">
                <p className="text-xs text-slate-400">Welcome, Sarah!</p>
                <div className="grid grid-cols-3 gap-1.5 text-center">
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2"><p className="text-[9px] text-slate-400">Progress</p><p className="text-sm font-bold text-emerald-400">85%</p></div>
                  <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-2"><p className="text-[9px] text-slate-400">Mock Score</p><p className="text-sm font-bold text-cyan-400">92%</p></div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-2"><p className="text-[9px] text-slate-400">Study</p><p className="text-sm font-bold text-purple-400">6.5h</p></div>
                </div>
                <div className="h-12 flex items-end gap-0.5">
                  {[3,5,4,6,7,5,8].map((h,i)=>(<div key={i} className="flex-1 bg-gradient-to-t from-purple-500 to-cyan-400 rounded-t" style={{height:`${h*10}%`}} />))}
                </div>
              </div>
            </div>
          </div>

          {/* Need Extra Support */}
          <div className="bg-slate-800/40 backdrop-blur rounded-3xl border border-slate-700/60 p-8 flex flex-col md:flex-row gap-6 items-center" data-testid="tutor-marketplace-card">
            <div className="flex-1 space-y-4">
              <h3 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Need Extra Support?</h3>
              <p className="text-sm text-slate-300">Connect with expert tutors through Edpies Tutor Marketplace.</p>
              <ul className="space-y-2 text-sm text-slate-300">
                {['Experienced, verified tutors', '1-to-1 or small group sessions', 'Flexible scheduling', 'Help when your child needs it most'].map((it) => (
                  <li key={it} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-400 mt-0.5" /> {it}
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                onClick={() => navigate('/resources')}
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 bg-transparent"
                data-testid="explore-tutors-cta"
              >
                Explore Tutors
              </Button>
            </div>
            <div className="w-full md:w-56 flex-shrink-0 relative">
              <div className="rounded-2xl overflow-hidden aspect-square shadow-lg ring-1 ring-slate-700">
                <img src={TUTOR_IMG} alt="Student learning with online tutor" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-3 left-3 right-3 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-xl shadow-lg p-2 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-slate-900 text-xs font-bold">ET</div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white truncate">Expert Tutor</p>
                  <p className="text-[10px] text-slate-400 flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> 4.9 (120)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-slate-900/40 border-y border-slate-800" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-10 text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Loved by Parents. Trusted by Families.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700/60" data-testid={`testimonial-${i}`}>
                <p className="text-3xl text-purple-500/40 font-serif leading-none">"</p>
                <p className="text-slate-300 text-sm leading-relaxed mt-2 mb-4">{t.quote}</p>
                <div className="flex items-center gap-3 pt-3 border-t border-slate-700">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center text-white font-bold text-sm">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.loc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-950 py-16 px-4 border-t border-slate-800" data-testid="final-cta-section">
        <div className="absolute inset-0 opacity-20" aria-hidden>
          <Sparkles className="absolute top-10 left-10 h-12 w-12 text-purple-400" />
          <Rocket className="absolute bottom-10 right-16 h-14 w-14 text-indigo-400" />
          <BookOpen className="absolute top-20 right-1/3 h-10 w-10 text-cyan-400" />
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" aria-hidden />
        <div className="relative max-w-4xl mx-auto text-center space-y-6 text-white">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Ready to Begin Your Child's <span className="bg-gradient-to-r from-purple-300 to-cyan-300 bg-clip-text text-transparent">Success Story?</span>
          </h2>
          <p className="text-lg text-slate-300">Start from just £5.99/month. Cancel anytime.</p>
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <Button
              onClick={handleStartTrial}
              className="h-12 px-7 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold shadow-xl shadow-purple-900/50"
              data-testid="final-cta-start-trial"
            >
              Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={handleChoosePlan}
              className="h-12 px-7 bg-transparent border-slate-500 text-white hover:bg-white/10"
              data-testid="final-cta-choose-plan"
            >
              Choose Your Plan
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 px-4 border-t border-slate-800" data-testid="site-footer">
        <div className="max-w-7xl mx-auto grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2 space-y-3">
            <div className="flex items-center gap-2">
              <EdpiesLogo size="sm" />
              <span className="text-lg font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
            </div>
            <p className="text-sm">AI-powered learning platform for UK exam success.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Product</h4>
            <ul className="space-y-2 text-sm">
              <li><button onClick={scrollToPlans} className="hover:text-white">Plans</button></li>
              <li><button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({behavior:'smooth'})} className="hover:text-white">How It Works</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><button onClick={() => navigate('/about')} className="hover:text-white">About Us</button></li>
              <li><button onClick={() => navigate('/contact')} className="hover:text-white">Contact Us</button></li>
              <li><button onClick={() => navigate('/faq')} className="hover:text-white">FAQ</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><button onClick={() => navigate('/privacy')} className="hover:text-white">Privacy</button></li>
              <li><button onClick={() => navigate('/terms')} className="hover:text-white">Terms</button></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-slate-800 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
          <p>© 2025 Edpies. All rights reserved.</p>
          <p>info@edpies.com</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
